
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import VWrapper from '@/components/shared/VWrapper.vue';
import MerchantStatusChip from '@/components/smoothrPay/MerchantStatusChip.vue';
import ProvisioningSteps from '@/components/smoothrPay/ProvisioningSteps.vue';
import BusinessLines from '@/components/smoothrPay/BusinessLines.vue';
import VenueStores from '@/components/smoothrPay/VenueStores.vue';
import VenuePaymentMethodsDialog from '@/components/smoothrPay/VenuePaymentMethodsDialog.vue';
import Notification from '@/mixins/Notification';
import api from '@/api/http/SmoothrPayApiService';
import { Permission } from '@/enums/Permission';
import { ProvisioningStep } from '@/enums/SmoothrPay';
import { isConfigurationOpen } from '@/util/smoothrPay';
import { Venue } from '@/interfaces/models/Venue';
import {
  AddVenuePaymentMethodsRequest,
  AttachSplitConfigurationRequest,
  CreateVenueStoreRequest,
  MerchantConfiguration,
  SplitConfiguration,
  UpdateBusinessLineRequest,
  VenuePaymentMethod,
  VenueStore,
} from '@/interfaces/models/SmoothrPay';

const app = namespace('app');

/**
 * One Smoothr Pay merchant: its Adyen provisioning, its business lines and the
 * venue stores it pays out for.
 *
 * Access mirrors the new backoffice. Every write is `smoothr.pay.onboarding`
 * server-side, and a closed or suspended configuration is read-only regardless;
 * on top of that, store management is admin-only, since assigning a venue
 * creates its Adyen store.
 */
@Component({
  components: {
    VWrapper,
    MerchantStatusChip,
    ProvisioningSteps,
    BusinessLines,
    VenueStores,
    VenuePaymentMethodsDialog,
  },
})
export default class SmoothrPayDetail extends mixins(Notification) {
  @app.State('venues') public venues!: Venue[];

  public configuration: MerchantConfiguration | null = null;
  public stores: VenueStore[] = [];
  public splitConfigurations: SplitConfiguration[] = [];
  public paymentMethods: VenuePaymentMethod[] = [];
  public busyStep: string = '';
  public methodsVisible: boolean = false;
  public methodsVenue: string = '';

  get id(): string {
    return this.$route.params.id;
  }

  get isOpen(): boolean {
    return isConfigurationOpen(this.configuration);
  }

  get canManage(): boolean {
    return this.$can(Permission.SMOOTHR_PAY_ONBOARDING) && this.isOpen;
  }

  /** Assigning a venue creates its Adyen store — admin work. */
  get canManageStores(): boolean {
    return this.$isAdmin() && this.isOpen;
  }

  get payInAllowed(): boolean {
    return !!(this.configuration && this.configuration.verification && this.configuration.verification.payInAllowed);
  }

  get payOutAllowed(): boolean {
    return !!(this.configuration && this.configuration.verification && this.configuration.verification.payOutAllowed);
  }

  get methodsVenueName(): string {
    const venue = this.venues.find((item: Venue) => item._id === this.methodsVenue);
    return venue ? venue.name : this.methodsVenue;
  }

  public mounted() {
    this.fetch();
  }

  public async fetch() {
    this.$startLoading('smoothrPay.detail');
    try {
      this.configuration = await api.findConfiguration(this.id);
      if (!this.configuration) {
        return;
      }
      await Promise.all([this.fetchStores(), this.fetchSplitConfigurations()]);
    } catch (error) {
      this.configuration = null;
      this.notifyError('smoothrPay.notification.loadError');
    } finally {
      this.$stopLoading('smoothrPay.detail');
    }
  }

  public async fetchStores() {
    try {
      const { data } = await api.getMerchantVenues(this.id);
      this.stores = data || [];
    } catch (error) {
      this.stores = [];
    }
  }

  /** Profiles are defined per merchant account, so scope the lookup to this one. */
  public async fetchSplitConfigurations() {
    try {
      const account = this.configuration ? this.configuration.merchantAccount : undefined;
      const { data } = await api.getSplitConfigurations(account);
      this.splitConfigurations = data || [];
    } catch (error) {
      this.splitConfigurations = [];
    }
  }

  public async runStep(step: ProvisioningStep) {
    this.busyStep = step;
    try {
      if (step === ProvisioningStep.KYC) {
        const { data } = await api.createHostedCheckoutUrl(this.id);
        // The link is valid for minutes only — hand it over straight away.
        window.open(data.hostedCheckoutUrl, '_blank');
        this.notifySuccess('smoothrPay.notification.kycStarted');
      } else {
        const { data } = await this.callStep(step);
        this.configuration = data;
        this.notifySuccess('smoothrPay.notification.stepDone');
      }
      await this.fetch();
    } catch (error) {
      this.notifyError('smoothrPay.notification.stepFailed');
    } finally {
      this.busyStep = '';
    }
  }

  public async saveBusinessLine(event: { businessLineId: string; payload: UpdateBusinessLineRequest }) {
    this.$startLoading('smoothrPay.businessLine');
    try {
      const { data } = await api.updateBusinessLine(this.id, event.businessLineId, event.payload);
      this.configuration = data;
      this.notifySuccess('smoothrPay.notification.businessLineUpdated');
    } catch (error) {
      this.notifyError('smoothrPay.notification.businessLineError');
    } finally {
      this.$stopLoading('smoothrPay.businessLine');
    }
  }

  public async assignVenue(payload: CreateVenueStoreRequest) {
    this.$startLoading('smoothrPay.store');
    try {
      await api.createVenueStore(payload);
      await this.fetchStores();
      this.notifySuccess('smoothrPay.notification.storeCreated');
    } catch (error) {
      this.notifyError('smoothrPay.notification.storeError');
    } finally {
      this.$stopLoading('smoothrPay.store');
    }
  }

  /**
   * Replaces the profile a store books through. The service requires a profile —
   * it cannot unlink one — and rejects a closed store, both of which the dialog
   * enforces before this runs.
   */
  public async attachSplit(event: { venue: string; payload: AttachSplitConfigurationRequest }) {
    this.$startLoading('smoothrPay.split');
    try {
      await api.attachSplitConfiguration(event.venue, event.payload);
      await this.fetchStores();
      this.notifySuccess('smoothrPay.notification.splitAttached');
    } catch (error) {
      this.notifyError('smoothrPay.notification.splitError');
    } finally {
      this.$stopLoading('smoothrPay.split');
    }
  }

  public async closeStore(store: VenueStore) {
    if (!window.confirm(this.$t('smoothrPay.stores.closeConfirm') as string)) {
      return;
    }
    this.$startLoading('smoothrPay.store');
    try {
      await api.closeVenueStore(store.venue);
      await this.fetchStores();
      this.notifySuccess('smoothrPay.notification.storeClosed');
    } catch (error) {
      this.notifyError('smoothrPay.notification.storeError');
    } finally {
      this.$stopLoading('smoothrPay.store');
    }
  }

  public async openMethods(store: VenueStore) {
    this.methodsVenue = store.venue;
    this.methodsVisible = true;
    this.paymentMethods = [];
    this.$startLoading('smoothrPay.methods');
    try {
      const { data } = await api.getVenuePaymentMethods(store.venue);
      this.paymentMethods = data || [];
    } catch (error) {
      this.notifyError('smoothrPay.notification.methodsError');
    } finally {
      this.$stopLoading('smoothrPay.methods');
    }
  }

  public async addPaymentMethods(payload: AddVenuePaymentMethodsRequest) {
    this.$startLoading('smoothrPay.methods.save');
    try {
      await api.addVenuePaymentMethods(this.methodsVenue, payload);
      const { data } = await api.getVenuePaymentMethods(this.methodsVenue);
      this.paymentMethods = data || [];
      this.notifySuccess('smoothrPay.notification.methodsRequested');
    } catch (error) {
      this.notifyError('smoothrPay.notification.methodsError');
    } finally {
      this.$stopLoading('smoothrPay.methods.save');
    }
  }

  public async removePaymentMethod(method: VenuePaymentMethod) {
    const id = method.adyen && method.adyen.paymentMethodId;
    if (!id) {
      return;
    }
    this.$startLoading('smoothrPay.methods.save');
    try {
      await api.removeVenuePaymentMethod(this.methodsVenue, id);
      this.paymentMethods = this.paymentMethods.filter((item: VenuePaymentMethod) => item._id !== method._id);
      this.notifySuccess('smoothrPay.notification.methodRemoved');
    } catch (error) {
      this.notifyError('smoothrPay.notification.methodsError');
    } finally {
      this.$stopLoading('smoothrPay.methods.save');
    }
  }

  public async invite() {
    this.$startLoading('smoothrPay.invite');
    try {
      const { data } = await api.invite(this.id);
      this.configuration = data;
      this.notifySuccess('smoothrPay.notification.invited');
    } catch (error) {
      this.notifyError('smoothrPay.notification.inviteError');
    } finally {
      this.$stopLoading('smoothrPay.invite');
    }
  }

  public async resetToDraft() {
    if (!window.confirm(this.$t('smoothrPay.detail.resetConfirm') as string)) {
      return;
    }
    this.$startLoading('smoothrPay.draft');
    try {
      const { data } = await api.resetToDraft(this.id);
      this.configuration = data;
      this.notifySuccess('smoothrPay.notification.resetToDraft');
    } catch (error) {
      this.notifyError('smoothrPay.notification.resetError');
    } finally {
      this.$stopLoading('smoothrPay.draft');
    }
  }

  private callStep(step: ProvisioningStep) {
    switch (step) {
      case ProvisioningStep.LEGAL_ENTITY:
        return api.createLegalEntity(this.id);
      case ProvisioningStep.BUSINESS_LINES:
        return api.createBusinessLines(this.id);
      case ProvisioningStep.ACCOUNT_HOLDER:
        return api.createAccountHolder(this.id);
      case ProvisioningStep.BALANCE_ACCOUNTS:
        return api.createBalanceAccounts(this.id);
      default:
        return Promise.reject(new Error(`Unsupported provisioning step: ${step}`));
    }
  }
}
